import React, {useState} from 'react';

import {withRouter, WithRouterProps} from 'react-router';

import {gt} from 'app/utils/i18n';

import {AddSignoffRulesButton} from './AddSignoffRulesButton';
import {ManageSignoffTemplateModal} from './ManageSignoffTemplateModal';
import {SignoffListOptionsBar} from './SignoffListOptionsBar';
import {TeamGroupedSignoffsList} from './TeamGroupedSignoffsList';

import type {TeamSerializedType} from 'sweet-types';

interface Props
  extends WithRouterProps<
    unknown,
    {groupby?: 'user' | 'doc'; ordering: string}
  > {
  team: TeamSerializedType;
  onFetch: () => Promise<unknown>;
}

export const TeamSignoffsSubPage: React.FC<Props> = withRouter(
  ({team, onFetch, location}) => {
    const groupBy = location.query.groupby || 'user';

    const [viewingAll, setViewingAll] = useState<boolean>(false);
    const [showManageRulesModal, setShowManageRulesModal] = useState(false);

    return (
      <div>
        <SignoffListOptionsBar
          team={team}
          requestDataRefetch={onFetch}
          viewingAll={viewingAll}
          setViewingAll={setViewingAll}
          showManageRulesModal={() => setShowManageRulesModal(true)}
        />
        <TeamGroupedSignoffsList
          paramsBuilder={({location: {query}}) => ({
            team_id: team.id,
            by: groupBy,
            tag: query.tag,
            search: query.search,
            ordering: query.ordering,
          })}
          pageParamName={`${groupBy}_page`}
          groupBy={groupBy}
          refreshList={onFetch}
          team={team}
          ordering={location.query.ordering}
          noItemsMessage={(perms, NoResultsMessage) =>
            team.signofftemplate == null ? (
              <NoResultsMessage
                classNameOverwrite="mt-4 text-center"
                head={gt('No sign off rules configured for this team')}
              >
                <>
                  <p>
                    {gt(
                      'Sign offs are a way for team members to acknowledge they have read and understood a procedure, policy or process.'
                    )}
                  </p>
                  {team.can_edit ? (
                    <div>
                      <AddSignoffRulesButton
                        showModal={() => setShowManageRulesModal(true)}
                        className="btn btn-outline-primary"
                      />
                    </div>
                  ) : null}
                </>
              </NoResultsMessage>
            ) : viewingAll ? (
              <NoResultsMessage
                classNameOverwrite="mt-4 text-center"
                head={gt('No sign off requests created yet')}
              >
                <>
                  <p>
                    {gt(
                      'Sign off rules are configured for this team but no sign off requests have been created yet.'
                    )}
                  </p>
                  <p>
                    {gt(
                      'Sign off requests are created when new team members or documents are added.'
                    )}
                  </p>
                </>
              </NoResultsMessage>
            ) : (
              <NoResultsMessage
                classNameOverwrite="mt-4 text-center"
                head={gt('No sign offs matching this search.')}
              >
                <>
                  <p>{gt('Reset filters to see all sign offs.')}</p>
                </>
              </NoResultsMessage>
            )
          }
        />
        <ManageSignoffTemplateModal
          key={`${team.id}-${team.signofftemplate ?? ''}`}
          show={showManageRulesModal}
          team={team}
          toggle={() => setShowManageRulesModal((s) => !s)}
        />
      </div>
    );
  }
);
